"use client";

import Link from "next/link";

interface TopicSiloMeshProps {
  currentPath?: string;
  isHindi?: boolean;
  className?: string;
}

interface SiloCluster {
  titleEn: string;
  titleHi: string;
  icon: string;
  links: Array<{ path: string; nameEn: string; nameHi: string; isInteractive?: boolean }>;
}

const SILO_CLUSTERS: SiloCluster[] = [
  {
    titleEn: "Birth Chart & Natal Analysis",
    titleHi: "जन्म कुंडली व मूल विश्लेषण",
    icon: "🪐",
    links: [
      {
        path: "/free-kundali/",
        nameEn: "Free Janam Kundali",
        nameHi: "मुफ्त जन्म कुंडली",
        isInteractive: true,
      },
      {
        path: "/kundli-matching/",
        nameEn: "36-Gun Kundali Milan",
        nameHi: "36 गुण मिलान",
        isInteractive: true,
      },
      {
        path: "/rashifal/",
        nameEn: "Daily Gochar Rashifal",
        nameHi: "आज का राशिफल",
        isInteractive: true,
      },
      {
        path: "/nakshatra/",
        nameEn: "27 Nakshatra Explorer",
        nameHi: "27 नक्षत्र कैलकुलेटर",
        isInteractive: true,
      },
    ],
  },
  {
    titleEn: "Planetary Timing & Panchang",
    titleHi: "शुभ समय व दैनिक पंचांग",
    icon: "⏱️",
    links: [
      {
        path: "/panchang/",
        nameEn: "Daily Panchang & Hora",
        nameHi: "दैनिक पंचांग व होरा",
        isInteractive: true,
      },
      {
        path: "/choghadiya/",
        nameEn: "Live Shubh Choghadiya",
        nameHi: "शुभ चौघड़िया मुहूर्त",
        isInteractive: true,
      },
      {
        path: "/rahu-kaal/",
        nameEn: "Rahu Kaal Timing",
        nameHi: "राहु काल समय",
        isInteractive: true,
      },
      {
        path: "/muhurat/",
        nameEn: "Shubh Muhurat Finder",
        nameHi: "शुभ मुहूर्त कैलकुलेटर",
        isInteractive: true,
      },
    ],
  },
  {
    titleEn: "Cosmic Cycles & Transit Timelines",
    titleHi: "ग्रह गोचर व दशा कालचक्र",
    icon: "🌌",
    links: [
      {
        path: "/sade-sati/",
        nameEn: "Shani Sade Sati Timeline",
        nameHi: "शनि साढ़े साती कैलकुलेटर",
        isInteractive: true,
      },
      {
        path: "/kaal-sarp-dosha/",
        nameEn: "Kaal Sarp Dosha Check",
        nameHi: "कालसर्प दोष विश्लेषण",
        isInteractive: true,
      },
      {
        path: "/mangal-dosha-calculator/",
        nameEn: "Mangal Dosha Analysis",
        nameHi: "मांगलिक दोष कैलकुलेटर",
        isInteractive: true,
      },
      {
        path: "/ashtakvarga/",
        nameEn: "Sarvashtakavarga Matrix",
        nameHi: "अष्टकवर्ग चक्र",
        isInteractive: true,
      },
    ],
  },
  {
    titleEn: "Vedic Remedies & AI Guidance",
    titleHi: "वैदिक उपाय व एआई ज्योतिषी",
    icon: "📿",
    links: [
      {
        path: "/ai-astrologer/",
        nameEn: "AI Astrologer Pandit-ji",
        nameHi: "एआई ज्योतिषी पंडित-जी",
        isInteractive: true,
      },
      {
        path: "/numerology/",
        nameEn: "Vedic Numerology Calculator",
        nameHi: "अंकशास्त्र कैलकुलेटर",
        isInteractive: true,
      },
      {
        path: "/gemstones/",
        nameEn: "Gemstone Upay Finder",
        nameHi: "रत्न परामर्श",
        isInteractive: true,
      },
      {
        path: "/prashna-kundali/",
        nameEn: "Prashna Kundali AI",
        nameHi: "प्रश्न कुंडली",
        isInteractive: true,
      },
    ],
  },
];

export default function VedicTopicSiloMesh({
  currentPath = "",
  isHindi = false,
  className = "",
}: TopicSiloMeshProps) {
  return (
    <nav
      aria-label={isHindi ? "वैदिक ज्योतिष विषय जाल" : "Vedic Astrology Topic Silo Mesh"}
      className={`my-10 rounded-2xl border border-border-default bg-surface-raised p-6 sm:p-8 shadow-xs ${className}`}
    >
      <div className="mb-6 pb-4 border-b border-border-default">
        <span className="text-caption font-bold uppercase tracking-wider text-intent-brand">
          {isHindi ? "संबंधित वैदिक गणना उपकरण" : "Connected Vedic Calculation Hubs"}
        </span>
        <h3 className="text-2xl font-bold font-serif text-text-primary mt-1">
          {isHindi
            ? "वैदिक ज्योतिष टूल क्लस्टर व त्वरित लिंक"
            : "Vedic Astrology Topic Network & Tools"}
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {SILO_CLUSTERS.map((silo, idx) => (
          <div key={idx} className="space-y-3">
            <div className="flex items-center gap-2 font-bold text-body-small text-text-primary">
              <span className="text-lg">{silo.icon}</span>
              <span>{isHindi ? silo.titleHi : silo.titleEn}</span>
            </div>

            <ul className="space-y-1.5 pl-2">
              {silo.links.map((item, lIdx) => {
                const targetHref =
                  isHindi && !item.path.startsWith("/hi") ? `/hi${item.path}` : item.path;
                const isCurrent = currentPath === item.path || currentPath === targetHref;

                return (
                  <li key={lIdx}>
                    <Link
                      href={targetHref}
                      className={`inline-flex items-center gap-1.5 text-caption transition-colors ${
                        isCurrent
                          ? "font-bold text-intent-brand pointer-events-none"
                          : "text-text-secondary hover:text-intent-brand hover:underline"
                      }`}
                    >
                      <span className="text-intent-brand">›</span>
                      <span>{isHindi ? item.nameHi : item.nameEn}</span>
                      {item.isInteractive && (
                        <span className="text-caption bg-surface-brand-subtle text-intent-brand px-1.5 py-0.5 rounded font-semibold">
                          Tool
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}
